import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  AssessmentAnswers,
  BUDGET_OPTIONS,
  GOAL_OPTIONS,
  MatchResult,
  REGION_OPTIONS,
  SerializableMatch,
  TIMELINE_OPTIONS,
  formatDuration,
  hydrateMatches,
  investmentInCny,
  toRoutableSlug,
} from "../../data/assessment";
import { submitAssessmentLead } from "../../api/assessment";
import { generatePdfFromElement } from "../../utils/generatePdf";
import AssessmentReportTemplate from "./AssessmentReportTemplate";
import { nvfoWechatQR, nvfoWhatsappQR } from "../../assets/imgExport";

type LocationState = {
  answers: AssessmentAnswers;
  matches: SerializableMatch[];
};

export default function AssessmentResultPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;

  const [showLeadForm, setShowLeadForm] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!state) navigate("/assessment", { replace: true });
  }, [state, navigate]);

  if (!state) return null;
  const { answers } = state;
  const matches: MatchResult[] = hydrateMatches(state.matches);

  async function handleDownload(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("请填写您的姓名");
      return;
    }
    if (!phone.trim() && !email.trim()) {
      setError("请填写联系电话或邮箱其中一项");
      return;
    }
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email)) {
      setError("请输入有效的邮箱地址");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await submitAssessmentLead({
        name,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        answers: answers as unknown as Record<string, string>,
        matches: matches.map((m) => ({
          country: m.data.country,
          program: m.data.immigrationType.title,
          score: m.score,
        })),
      });
      if (reportRef.current) {
        await generatePdfFromElement(reportRef.current, `NVFO_移民方案定制报告_${name}.pdf`, {
          orientation: "landscape",
        });
      }
      setShowLeadForm(false);
    } catch (err) {
      setError("生成失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-c-gray min-h-screen">
      <div className="max-width-centered px-6 sm:px-10 py-16 sm:py-24">
        <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-main/70 border border-main/30 rounded-full px-4 py-1.5 mb-6">
          AI 智能匹配 · 测评结果
        </p>
        <h1 className="text-3xl sm:text-4xl mb-3 text-secondary-dark font-semibold font-sans">
          您的移民方案定制推荐
        </h1>
        <p className="text-secondary-dark/70 leading-relaxed mb-4">
          根据您的预算、目标、意向地区与办理时效，AI 智能匹配系统为您筛选出以下最相关的方案。
        </p>
        <AnswersSummary answers={answers} />

        <div className="flex flex-col gap-5 mt-10">
          {matches.map((match, index) => (
            <MatchCard key={match.slug + match.category} match={match} rank={index + 1} />
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mt-12">
          <button
            className="btn bg-main hover:bg-secondary-dark text-white px-8 py-4"
            onClick={() => setShowLeadForm(true)}
          >
            下载 PDF 定制报告
          </button>
          <Link
            to="/assessment"
            className="btn bg-white border border-main text-main px-8 py-4 text-center hover:bg-main/5"
          >
            重新测评
          </Link>
        </div>

        <div className="flex items-center gap-10 mt-16 pt-10 border-t border-fourth-light">
          <div className="flex flex-col items-center gap-2">
            <img src={nvfoWechatQR} alt="微信咨询二维码" className="h-24 w-auto max-w-none rounded-lg border border-fourth-light" />
            <span className="text-xs text-secondary-light">微信咨询</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <img src={nvfoWhatsappQR} alt="WhatsApp咨询二维码" className="h-24 w-auto max-w-none rounded-lg border border-fourth-light" />
            <span className="text-xs text-secondary-light">WhatsApp咨询</span>
          </div>
        </div>
      </div>

      {showLeadForm && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 px-6">
          <div className="bg-white rounded-xl max-w-md w-full p-8 relative">
            <button
              className="absolute top-4 right-4 text-secondary-dark/50 hover:text-secondary-dark"
              onClick={() => setShowLeadForm(false)}
              aria-label="关闭"
            >
              ✕
            </button>
            <h2 className="text-xl font-semibold mb-2">留下联系方式，获取完整报告</h2>
            <p className="text-sm text-secondary-dark/60 mb-6">
              我们会将 PDF 报告直接生成供您下载，顾问团队也会主动与您联系，提供一对一咨询。
            </p>
            <form className="flex flex-col gap-4" onSubmit={handleDownload}>
              <input
                className="bg-white border border-fourth-light rounded-lg px-4 py-3"
                placeholder="您的姓名"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <input
                className="bg-white border border-fourth-light rounded-lg px-4 py-3"
                placeholder="联系电话（电话与邮箱至少填写一项）"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <input
                className="bg-white border border-fourth-light rounded-lg px-4 py-3"
                placeholder="邮箱地址（电话与邮箱至少填写一项）"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="btn bg-main hover:bg-secondary-dark text-white py-3 mt-2 disabled:opacity-60"
              >
                {submitting ? "生成中…" : "获取 PDF 报告"}
              </button>
            </form>
          </div>
        </div>
      )}

      <AssessmentReportTemplate ref={reportRef} name={name} matches={matches} />
    </div>
  );
}

function AnswersSummary({ answers }: { answers: AssessmentAnswers }) {
  const budget = BUDGET_OPTIONS.find((o) => o.key === answers.budget)?.label;
  const goal = GOAL_OPTIONS.find((o) => o.key === answers.goal)?.label;
  const region = REGION_OPTIONS.find((o) => o.key === answers.region)?.label;
  const timeline = TIMELINE_OPTIONS.find((o) => o.key === answers.timeline)?.label;

  return (
    <div className="flex flex-wrap gap-2 mt-4">
      {[budget, goal, region, timeline].filter(Boolean).map((label) => (
        <span
          key={label}
          className="text-xs bg-main/10 text-main px-3 py-1.5 rounded-full"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function MatchCard({ match, rank }: { match: MatchResult; rank: number }) {
  const cny = investmentInCny(match.data);
  const routableSlug = toRoutableSlug(match.slug, match.category);
  const detailPath =
    match.category === "residence"
      ? `/program/residence/${routableSlug}`
      : `/program/passport/${routableSlug}`;

  return (
    <div className="bg-white rounded-xl border border-fourth-light p-6 flex flex-col sm:flex-row gap-6">
      <img
        src={match.data.image.flag}
        alt={match.data.country}
        className="w-16 h-16 rounded-lg object-cover border border-fourth-light shrink-0"
      />
      <div className="flex-1">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs text-secondary-light">
              #{rank} 推荐 ·{" "}
              <span
                className={
                  match.category === "passport" ? "text-amber-600" : "text-main"
                }
              >
                {match.data.immigrationType.type}
              </span>
            </p>
            <h3 className="text-xl font-semibold mt-1">
              {match.data.country} · {match.data.immigrationType.title}
            </h3>
          </div>
          <div className="text-right shrink-0">
            <p className="text-2xl font-bold text-main">{match.score}%</p>
            <p className="text-xs text-secondary-light">AI 匹配度</p>
          </div>
        </div>

        <div className="w-full h-1.5 bg-c-gray rounded-full overflow-hidden mt-3">
          <div
            className="h-full bg-main rounded-full"
            style={{ width: `${match.score}%` }}
          />
        </div>

        <p className="text-sm text-secondary-dark/70 mt-3">
          {cny > 0 ? `投资门槛约 ¥${Math.round(cny / 10000)} 万元起` : "无固定投资门槛"} · 办理周期{" "}
          {formatDuration(match.data.process.duration)}
        </p>

        <ul className="flex flex-wrap gap-2 mt-3">
          {match.reasons.map((reason, i) => (
            <li
              key={i}
              className="text-xs bg-c-gray text-secondary-dark/80 px-3 py-1 rounded-full"
            >
              {reason}
            </li>
          ))}
        </ul>

        <Link
          to={detailPath}
          className="inline-flex items-center gap-1 text-main text-sm mt-4 hover:underline"
        >
          查看项目详情 →
        </Link>
      </div>
    </div>
  );
}
